import { useEffect, useId, useRef, useState } from 'react';
import gsap from 'gsap';
import { MACRO_ZONE } from '../../common/utils/geoItalia';
import {
  REGION_PATHS,
  ITALIA_OUTLINE,
  SARDEGNA_OUTLINE,
  SICILIA_OUTLINE,
  MACRO_BANDS,
  MACRO_VIEWPORT,
  FULL_VIEWPORT,
  REGION_CENTROIDS,
  regionMacro,
  VIEWBOX,
} from './italia/regionPaths';

const REGION_LABELS = {
  'valle-aosta': "VdA",
  piemonte: 'Piem',
  liguria: 'Lig',
  lombardia: 'Lom',
  trentino: 'TAA',
  veneto: 'Ven',
  friuli: 'FVG',
  emilia: 'Em-R',
  toscana: 'Tos',
  umbria: 'Umb',
  marche: 'Mar',
  lazio: 'Laz',
  abruzzo: 'Abr',
  molise: 'Mol',
  campania: 'Cam',
  puglia: 'Pug',
  basilicata: 'Bas',
  calabria: 'Cal',
  sicilia: 'Sic',
  sardegna: 'Sar',
};

/**
 * Cartina interattiva d'Italia con animazioni GSAP.
 * Zoom controllato: vista completa di default, pan leggero per macro-zona.
 */
export default function ItaliaMap({
  activeRegion = null,
  activeMacro = 'centro',
  filterRegion = null,
  filterMacro = null,
  onRegionClick,
  onMacroClick,
  expanded = false,
  interactive = true,
  showControls = null,
  className = '',
}) {
  const uid = useId().replace(/:/g, '');
  const containerRef = useRef(null);
  const panRef = useRef(null);
  const [userZoom, setUserZoom] = useState(1);
  const [viewMode, setViewMode] = useState('full'); // 'full' | 'macro'

  const controlsVisible = showControls ?? (interactive && !expanded);

  const macroViewport = MACRO_VIEWPORT[activeMacro] || FULL_VIEWPORT;
  const baseViewport = viewMode === 'macro' && !expanded ? macroViewport : FULL_VIEWPORT;
  const targetScale = baseViewport.scale * userZoom * (expanded ? 1.08 : 1);

  useEffect(() => {
    if (!panRef.current) return;
    gsap.to(panRef.current, {
      x: baseViewport.x,
      y: baseViewport.y,
      scale: targetScale,
      duration: 0.85,
      ease: 'power2.inOut',
      transformOrigin: '200px 240px',
    });
  }, [baseViewport.x, baseViewport.y, targetScale, activeMacro, viewMode, expanded]);

  useEffect(() => {
    if (!containerRef.current) return;
    gsap.fromTo(containerRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power2.out' });
  }, []);

  const zoomIn = () => setUserZoom((z) => Math.min(z + 0.15, 1.6));
  const zoomOut = () => setUserZoom((z) => Math.max(z - 0.15, 0.85));
  const resetView = () => {
    setUserZoom(1);
    setViewMode('full');
  };

  const handleMacroClick = (id) => {
    setViewMode('macro');
    onMacroClick?.(id);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {controlsVisible && (
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <button
            type="button"
            onClick={zoomOut}
            className="w-7 h-7 rounded-lg bg-white/95 shadow text-sm font-bold text-gray-600 hover:bg-white"
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            onClick={resetView}
            className="px-2 h-7 rounded-lg bg-white/95 shadow text-[10px] font-semibold text-[#c78244] hover:bg-white"
          >
            Italia
          </button>
          <button
            type="button"
            onClick={zoomIn}
            className="w-7 h-7 rounded-lg bg-white/95 shadow text-sm font-bold text-gray-600 hover:bg-white"
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      )}

      <div className="overflow-visible rounded-xl">
        <svg
          viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`}
          className="w-full h-full"
          role="img"
          aria-label="Cartina d'Italia"
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id={`${uid}-sea`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#dceef8" />
              <stop offset="100%" stopColor="#b8d8ec" />
            </linearGradient>
            <linearGradient id={`${uid}-land`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#faf3e8" />
              <stop offset="100%" stopColor="#f0e4d0" />
            </linearGradient>
            <filter id={`${uid}-glow`} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect width={VIEWBOX.w} height={VIEWBOX.h} fill={`url(#${uid}-sea)`} rx="12" />

          <g ref={panRef} style={{ transformOrigin: '200px 240px' }}>
            {interactive && Object.entries(MACRO_BANDS).map(([id, band]) => (
              <rect
                key={`band-${id}`}
                x={20}
                y={band.y}
                width={360}
                height={band.height}
                rx={8}
                fill={filterMacro === id ? `${MACRO_ZONE[id]?.color}22` : 'transparent'}
                stroke={filterMacro === id ? MACRO_ZONE[id]?.color : 'rgba(255,255,255,0.15)'}
                strokeWidth={filterMacro === id ? 2 : 1}
                strokeDasharray={filterMacro === id ? 'none' : '5 4'}
                className="cursor-pointer"
                onClick={() => handleMacroClick(id)}
              />
            ))}

            <path d={ITALIA_OUTLINE} fill="rgba(0,0,0,0.05)" transform="translate(1,2)" />
            <path d={ITALIA_OUTLINE} fill={`url(#${uid}-land)`} stroke="#b8956a" strokeWidth={1.2} />
            <path d={SARDEGNA_OUTLINE} fill={`url(#${uid}-land)`} stroke="#b8956a" strokeWidth={1} />
            <path d={SICILIA_OUTLINE} fill={`url(#${uid}-land)`} stroke="#b8956a" strokeWidth={1} />

            {Object.entries(REGION_PATHS).map(([id, d]) => {
              const macro = regionMacro(id);
              const isActive = activeRegion === id;
              const isFiltered = filterRegion === id;
              const isDimmed = (filterRegion && filterRegion !== id) || (filterMacro && regionMacro(id) !== filterMacro);
              const baseColor = MACRO_ZONE[macro]?.color || '#c78244';
              const fill = isActive || isFiltered ? baseColor : `${baseColor}${isDimmed ? '33' : '77'}`;

              return (
                <g key={id}>
                  {/* Area cliccabile allargata (invisibile) */}
                  {interactive && (
                    <path
                      d={d}
                      fill="transparent"
                      stroke="transparent"
                      strokeWidth={12}
                      className="cursor-pointer"
                      onClick={() => onRegionClick?.(id)}
                    />
                  )}
                  <path
                    d={d}
                    fill={fill}
                    stroke={isActive ? '#fff' : isFiltered ? baseColor : 'rgba(255,255,255,0.65)'}
                    strokeWidth={isActive ? 2.2 : isFiltered ? 1.8 : 0.7}
                    filter={isActive ? `url(#${uid}-glow)` : undefined}
                    className={interactive ? 'cursor-pointer pointer-events-none' : ''}
                    style={{ transition: 'fill 0.25s ease, stroke 0.25s ease' }}
                  />
                  {(isActive || isFiltered) && REGION_CENTROIDS[id] && (
                    <text
                      x={REGION_CENTROIDS[id].x}
                      y={REGION_CENTROIDS[id].y}
                      textAnchor="middle"
                      fontSize="9"
                      fontWeight="700"
                      fill="#fff"
                      className="pointer-events-none select-none"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
                    >
                      {REGION_LABELS[id]}
                    </text>
                  )}
                </g>
              );
            })}

            {activeRegion && REGION_CENTROIDS[activeRegion] && (
              <g transform={`translate(${REGION_CENTROIDS[activeRegion].x}, ${REGION_CENTROIDS[activeRegion].y})`}>
                <circle r={5} fill="#F6CF48" stroke="#c78244" strokeWidth={1.5} />
                <circle r={10} fill="none" stroke="#F6CF48" strokeWidth={1} opacity={0.45}>
                  <animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.5;0.15;0.5" dur="2s" repeatCount="indefinite" />
                </circle>
              </g>
            )}
          </g>
        </svg>
      </div>

      {activeMacro && !expanded && (
        <div className="absolute bottom-2 left-2 right-2 text-center pointer-events-none">
          <span className="text-[10px] uppercase tracking-widest text-[#9a5f2f] font-semibold bg-white/92 backdrop-blur px-3 py-1 rounded-full shadow-sm">
            {MACRO_ZONE[activeMacro]?.label}
            {viewMode === 'full' ? ' · vista completa' : ''}
          </span>
        </div>
      )}
    </div>
  );
}
